import React, { useEffect } from 'react';
import styled from 'types/theme.types';
import { Menu } from 'antd';
import ScrollIntoViewIfNeeded from 'react-scroll-into-view-if-needed';

import type { MenuProps as AntdMenuProps } from 'antd/lib/menu';

import { useNavigate, useLocation } from 'react-router-dom';

export interface MenuProps {
  setActiveMenu: React.Dispatch<string>;
  activeMenu: string;
  menus: readonly string[];
  children: React.ReactNode;
  renderMenuItem?: (key: string) => React.ReactNode;
  customActions?: React.ReactNode;
}

export const TAB_SEARCH_PARAM_NAME = 'tabname';
export const TAB_ANCHOR_HASH = '#tab';

const TabsMenu: React.FunctionComponent<MenuProps> = ({
  activeMenu,
  setActiveMenu,
  menus,
  children,
  renderMenuItem,
  customActions
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.has(TAB_SEARCH_PARAM_NAME)) {
      const tab = searchParams.get(TAB_SEARCH_PARAM_NAME);

      if (tab && menus.includes(tab)) {
        setActiveMenu(tab);
      }
    }
  }, [location, setActiveMenu, menus]);

  const setActiveMenuAndAddSearchParam: AntdMenuProps['onClick'] = event => {
    const key = event.key.toString();
    const searchParams = new URLSearchParams(location.search);

    searchParams.set(TAB_SEARCH_PARAM_NAME, key);
    location.search = searchParams.toString();
    navigate(location);

    setActiveMenu(key);
  };

  // The style is applied inline because the Menu adjusts the items inside a dropdown in Tablet and smaller viewports and the Styled CSS is not applied to those items
  const renderMenuItemFunc =
    renderMenuItem || ((menu: string) => <div style={{ textTransform: 'uppercase' }}>{menu}</div>);

  return (
    <Container>
      <ScrollIntoViewIfNeeded
        active={location.hash === TAB_ANCHOR_HASH}
        options={{ behavior: 'smooth', block: 'start', scrollMode: 'always' }}
      />
      <MenuContainer>
        <StyledMenu
          onClick={setActiveMenuAndAddSearchParam}
          selectedKeys={[activeMenu]}
          mode="horizontal"
          items={menus.map(menuKey => ({
            key: menuKey,
            label: renderMenuItemFunc(menuKey),
            'data-e2e': `menu-${menuKey}`
          }))}
        />
        {customActions}
      </MenuContainer>
      <ContentContainer>{children}</ContentContainer>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const MenuContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 2px solid ${({ theme }) => theme.menuBorder} !important;
  height: 47px;
`;

const ContentContainer = styled.div`
  padding: 18px 0;
`;

const StyledMenu = styled(Menu)`
  background: transparent !important;
  border-bottom-width: 0 !important;
  margin-bottom: -1px;
  flex: 1;
  overflow: hidden;

  .ant-menu-item {
    color: ${({ theme }) => theme.menuItemText} !important;
    border-bottom-width: 4px !important;
    border-bottom-color: transparent;
    font-size: 15px;
    font-weight: 600;
    line-height: 31px;
    letter-spacing: -0.08px;
    text-transform: uppercase;
    z-index: 2;

    &-selected:after,
    &:hover:after {
      color: ${({ theme }) => theme.primaryBlack} !important;
      border-bottom-width: 3px !important;
      border-bottom-color: ${({ theme }) => theme.primaryBlack} !important;
    }
  }
`;

export default TabsMenu;
